import React, { useEffect, useRef } from 'react';
import { Chart, ChartConfiguration, registerables } from 'chart.js';

Chart.register(...registerables);

interface QuarterlyData {
    quarter: string;
    vouchersAssigned: number;
    vouchersRedeemed: number;
    passRate: number;
    redemptionRate: number;
}

interface QuarterlyComparisonChartProps {
    data: QuarterlyData[];
    chartType?: 'passRate' | 'redemption' | 'volume';
    title?: string;
}

export const QuarterlyComparisonChart: React.FC<QuarterlyComparisonChartProps> = ({
    data,
    chartType = 'passRate',
    title
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const chartRef = useRef<Chart | null>(null);

    useEffect(() => {
        if (!canvasRef.current || !data.length) return;

        // Destroy existing chart
        if (chartRef.current) {
            chartRef.current.destroy();
        }

        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) return;

        let config: ChartConfiguration;

        if (chartType === 'passRate') {
            config = {
                type: 'line',
                data: {
                    labels: data.map(d => d.quarter),
                    datasets: [{
                        label: 'Pass Rate (%)',
                        data: data.map(d => d.passRate),
                        borderColor: '#10B981',
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        borderWidth: 3,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#10B981',
                        pointBorderColor: '#ffffff',
                        pointBorderWidth: 2,
                        pointRadius: 6
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: {
                            display: true,
                            text: title || 'Pass Rate Comparison by Quarter',
                            font: { size: 16, weight: 'bold' }
                        },
                        legend: { display: false }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            max: 100,
                            ticks: {
                                callback: function (value) {
                                    return value + '%';
                                }
                            }
                        }
                    }
                }
            };
        } else if (chartType === 'redemption') {
            config = {
                type: 'bar',
                data: {
                    labels: data.map(d => d.quarter),
                    datasets: [
                        {
                            label: 'Assigned',
                            data: data.map(d => d.vouchersAssigned),
                            backgroundColor: '#3B82F6',
                            borderRadius: 4
                        },
                        {
                            label: 'Redeemed',
                            data: data.map(d => d.vouchersRedeemed),
                            backgroundColor: '#10B981',
                            borderRadius: 4
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: {
                            display: true,
                            text: title || 'Voucher Volume by Quarter',
                            font: { size: 16, weight: 'bold' }
                        }
                    },
                    scales: {
                        y: { beginAtZero: true }
                    }
                }
            };
        } else {
            // Volume chart
            config = {
                type: 'doughnut',
                data: {
                    labels: data.map(d => d.quarter),
                    datasets: [{
                        data: data.map(d => d.vouchersRedeemed),
                        backgroundColor: [
                            '#3B82F6',
                            '#10B981',
                            '#F59E0B',
                            '#EF4444',
                            '#8B5CF6',
                            '#06B6D4'
                        ],
                        borderWidth: 2,
                        borderColor: '#ffffff'
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: {
                            display: true,
                            text: title || 'Redemption Distribution by Quarter',
                            font: { size: 16, weight: 'bold' }
                        }
                    }
                }
            };
        }

        chartRef.current = new Chart(ctx, config);

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        };
    }, [data, chartType, title]);

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="relative h-64">
                <canvas ref={canvasRef}></canvas>
            </div>

            {/* Summary Stats */}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                {data.map((quarter, index) => (
                    <div key={index} className="text-center">
                        <div className="text-sm font-medium text-gray-600">{quarter.quarter}</div>
                        <div className="text-lg font-bold text-gray-900">
                            {chartType === 'passRate' ? `${quarter.passRate}%` : quarter.vouchersRedeemed}
                        </div>
                        <div className="text-xs text-gray-500">
                            {chartType === 'passRate' ? 'Pass Rate' : 'Redeemed'}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};